import "./RoutineBar.css";
import { useEffect, useRef, useState } from "preact/hooks";
import { useStore } from "../../state/useStore.ts";
import { store } from "../../state/index.ts";
import { K } from "../../state/keys.ts";
import { isEmptyDay, openingOfLine, openingPlays, routineParams } from "../../domain/routine.ts";
import type { RoutineDay, RoutineItem } from "../../domain/routine.ts";
import * as routine from "../../services/routine.ts";
import * as practice from "../../legacy/practice.js";
import { t } from "../../i18n/routine.ts";

/**
 * The routine mode of a board page (docs/DAILY-ROUTINE.md): the study playing the day's opening, or Your positions
 * playing one of its moments, opened from /day with `?routine=<day>&slot=<n>`. It says which exercise of the day
 * this is and leads back. On the study it also counts the plays: the opening is played three times before the day
 * moves on (RoutineSlot.OPENING_PLAYS), and every practice finished and counted is one more.
 *
 * Nothing at all without those parameters: the page is the tool it always was.
 */
export function RoutineBar() {
    const params = routineParams(location.search);
    const [day, setDay] = useState<RoutineDay | null>(null);
    const loggedIn = !!useStore(K.user.currentUser);
    const line = useStore(K.practice.line);

    // From /day the address says which part this is; anywhere else the study asks whether the line on the board is
    // today's opening, so its practices count wherever they are played
    useEffect(() => {
        if (day || !loggedIn || (!params && !line)) return;
        routine.fetchDay(params ? params.day : "today").then((answer) => {
            if (answer && !isEmptyDay(answer)) setDay(answer);
        }).catch((err) => console.warn("[routine] Could not read the day", err));
    }, [loggedIn, !!line]);

    const item = !day ? null
        : params ? day.items.find((i) => i.slot === params.slot) ?? null
            : openingOfLine(day, line);
    if (!day || !item) return null;
    return <DayBar day={day} initial={item} />;
}

function DayBar({ day, initial }: { day: RoutineDay; initial: RoutineItem }) {
    const [item, setItem] = useState<RoutineItem>(initial);
    const back = `/day/${day.day}`;
    const label = t("barLabel", item.slot, day.items.length);

    if (item.kind !== "opening") {
        return (
            <aside class="routine-bar">
                <div class="routine-bar__text">
                    <span class="routine-bar__label">{label}</span>
                    <span>{t("barMoment")}</span>
                </div>
                <a class="btn btn-ghost btn-sm" href={back}>{t("backToDay")}</a>
            </aside>
        );
    }
    return <OpeningBar day={day} item={item} onItem={setItem} label={label} back={back} />;
}

function OpeningBar({ day, item, onItem, label, back }: {
    day: RoutineDay; item: RoutineItem; onItem: (item: RoutineItem) => void; label: string; back: string;
}) {
    const status = useStore(K.practice.status);
    const result = useStore(K.practice.result);
    // One play per practice finished: the result object is new each time a practice ends
    const counted = useRef<unknown>(null);

    useEffect(() => {
        if (status !== "finished" || !result || counted.current === result) return;
        counted.current = result;
        // Every practice the account keeps counts, a retry from a slip too: the server adds up the same practices
        // (RoutineController.syncPlays), and the two must not disagree
        if (!result.counted) return;
        routine.addPlay(day.day, item.slot).then(onItem)
            .catch((err) => console.warn("[routine] Could not count the play", err));
    }, [status, result]);

    const plays = openingPlays(item, day);
    // The result of a practice offers the way on too (PracticeResult), from what this bar knows
    useEffect(() => {
        store.set("studyLine.routine", { back, played: plays.played, needed: plays.needed, canContinue: plays.canContinue });
        return () => store.set("studyLine.routine", null);
    }, [plays.played, plays.canContinue]);

    return (
        <aside class="routine-bar" data-ready={plays.canContinue || undefined}>
            <div class="routine-bar__text">
                <span class="routine-bar__label">{label}</span>
                <span>{t("barOpening", plays.played, plays.needed)}</span>
            </div>
            <div class="toolbar">
                {status === "finished" && (
                    <button type="button" class="btn btn-ghost btn-sm" onClick={() => void practice.restart()}>
                        {t("playAgain")}
                    </button>
                )}
                {plays.canContinue
                    ? <a class="btn btn-primary btn-sm" href={back}>{t("next")}</a>
                    : <a class="btn btn-ghost btn-sm" href={back}>{t("backToDay")}</a>}
            </div>
        </aside>
    );
}
